// 데모 계정(username: demo)의 섹션(이름 붙여 저장한 칸 묶음).
// BE `/api/farms/{id}/sections` 를 타지 않고 브라우저 localStorage 에만 둔다.
//
// 섹션은 농장 자산이라 실계정에서는 서버에 생긴다. 데모가 그대로 타면 시연 중 만든 섹션이
// 실제 농장에 남는다. 섹션은 단건 미션 "불러오기"(DroneControl)와 예약(SchedulerPanel)이 같이 쓴다.
//
// 화면이 읽는 필드는 실계정 응답과 같게 맞춘다(id/name/altitude/cellIds/scheduleCount).
// 농장별로 나눠 담는다 — cellIds 가 그 농장의 칸 id 라 섞이면 엉뚱한 칸이 선택된다.

import { listDemoSchedules } from "./demoSchedules";

const STORAGE_KEY = "demo-sections";

// 실계정 에러를 읽는 쪽(SectionPanel 의 errorCode)이 error.response.data.error.code 를
// 보므로, 같은 모양으로 던져서 분기 코드를 그대로 쓴다.
export class DemoSectionError extends Error {
  constructor(code) {
    super(code);
    this.name = "DemoSectionError";
    this.response = { data: { error: { code } } };
  }
}

const readAll = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

const writeAll = (map) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // 저장 실패는 화면 동작을 막지 않는다.
  }
};

const keyOf = (farmId) => String(farmId ?? "");

const read = (farmId) => {
  const list = readAll()[keyOf(farmId)];
  return Array.isArray(list) ? list : [];
};

const write = (farmId, list) => {
  const map = readAll();
  map[keyOf(farmId)] = list;
  writeAll(map);
  return list;
};

// 실계정은 scheduleCount 를 서버가 세어 준다. 0 이 아니면 SectionPanel 이 삭제를 막는다.
// 데모 예약도 같은 브라우저에 있으므로 여기서 세면 실계정과 같은 규칙이 된다.
const scheduleCountOf = (sectionId) =>
  listDemoSchedules().filter((s) => String(s.sectionId) === String(sectionId)).length;

const decorate = (section) => ({
  ...section,
  cellIds: Array.isArray(section.cellIds) ? section.cellIds : [],
  scheduleCount: scheduleCountOf(section.id),
});

export const listDemoSections = (farmId) => {
  if (farmId == null) return [];
  return read(farmId).map(decorate);
};

const sameName = (a, b) => a.trim().toLowerCase() === b.trim().toLowerCase();

export const saveDemoSection = (farmId, { name, altitude, cellIds }) => {
  const trimmed = String(name ?? "").trim();
  const list = read(farmId);
  // 실계정의 SECTION-002 와 같은 규칙. 이름이 겹치면 불러오기에서 어느 쪽인지 알 수 없다.
  if (list.some((item) => sameName(item.name, trimmed))) {
    throw new DemoSectionError("SECTION-002");
  }
  const created = {
    id: `demo-section-${Date.now()}`,
    name: trimmed,
    altitude: Number(altitude) || 0,
    cellIds: Array.isArray(cellIds) ? [...cellIds] : [],
  };
  write(farmId, [...list, created]);
  return decorate(created);
};

export const renameDemoSection = (farmId, id, name) => {
  const trimmed = String(name ?? "").trim();
  const list = read(farmId);
  if (list.some((item) => item.id !== id && sameName(item.name, trimmed))) {
    throw new DemoSectionError("SECTION-002");
  }
  return write(
    farmId,
    list.map((item) => (item.id === id ? { ...item, name: trimmed } : item))
  );
};

export const removeDemoSection = (farmId, id) => {
  // 실계정의 SECTION-003. 화면도 먼저 막지만, 예약이 걸린 섹션이 조용히 사라지면 안 된다.
  if (scheduleCountOf(id) > 0) throw new DemoSectionError("SECTION-003");
  return write(farmId, read(farmId).filter((item) => item.id !== id));
};
